// src/app/services/google-pay.service.ts
import { Injectable } from '@angular/core';
import { environment } from '@env';
import { BehaviorSubject } from 'rxjs';
import * as Sentry from '@sentry/angular';

declare var google: any;

@Injectable({
  providedIn: 'root',
})
export class GooglePayService {
  public googlePayClient: any;
  private scriptLoaded$ = new BehaviorSubject<boolean>(false);

  constructor() {
    this.loadGooglePayScript().then(() => {
      this.initGooglePay();
    }).catch(error => {
      // Google Pay failed to load - this is often expected due to ad blockers
      // Don't treat this as a critical error since app should work without Google Pay
      console.info('Google Pay not available (likely blocked by ad blocker or network) - app will continue without Google Pay');

      // Set a flag indicating Google Pay is unavailable
      this.scriptLoaded$.next(false);

      // Log as info breadcrumb only (not as error/warning)
      Sentry.addBreadcrumb({
        category: 'google-pay',
        message: 'Google Pay initialization failed - app will continue without Google Pay',
        level: 'info',
        data: {
          environment: environment.googlePayEnvironment,
          errorMessage: error?.message || 'Script load failed'
        }
      });
    });
  }

  /**
   * Dynamically loads the Google Pay script.
   */
  private loadGooglePayScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      // Check if the script is already loaded
      if (typeof google !== 'undefined' && google.payments && google.payments.api) {
        this.scriptLoaded$.next(true);
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://pay.google.com/gp/p/js/pay.js';
      script.async = true;
      script.defer = true;
      script.onload = () => {
        this.scriptLoaded$.next(true);
        resolve();
      };
      script.onerror = (event) => {
        // Prevent the error from bubbling up to global error handlers
        if (event && typeof event === 'object' && 'preventDefault' in event) {
          (event as Event).preventDefault();
        }

        // Don't log as exception - this is often expected (ad blockers, network issues, etc.)
        // Only log as warning since Google Pay failure doesn't break core app functionality
        Sentry.addBreadcrumb({
          category: 'google-pay',
          message: 'Google Pay script failed to load (likely ad blocker or network issue)',
          level: 'info', // Downgrade to info since this is expected
          data: {
            scriptUrl: 'https://pay.google.com/gp/p/js/pay.js',
            environment: environment.googlePayEnvironment,
            onlineStatus: navigator.onLine
          }
        });

        // Don't capture as Sentry message in production - this is too noisy and expected
        // Ad blockers, corporate firewalls, etc. commonly block Google Pay

        // Reject with a simple error message (not the raw event)
        reject(new Error('Google Pay script load failed'));
      };

      // Add error handler for manifest
      const manifestLink = document.createElement('link');
      manifestLink.rel = 'manifest';
      manifestLink.href = 'https://pay.google.com/gp/p/web_manifest.json';
      manifestLink.onerror = (e) => {
        // Only log manifest errors in production
        if (environment.production) {
          Sentry.addBreadcrumb({
            category: 'google-pay',
            message: 'Google Pay manifest not found',
            level: 'warning'
          });
        }
      };
      document.head.appendChild(manifestLink);

      document.head.appendChild(script);
    });
  }

  /**
   * Initializes the Google Pay client.
   */
  private initGooglePay() {
    if (typeof google !== 'undefined' && google.payments && google.payments.api) {
      this.googlePayClient = new google.payments.api.PaymentsClient({
        environment: environment.googlePayEnvironment, // Use environment variable
      });
    } else {
      // Only log initialization errors in production
      if (environment.production) {
        Sentry.captureMessage('Google Pay library not loaded', {
          level: 'error',
          extra: {
            environment: environment.googlePayEnvironment
          }
        });
      }
    }
  }

  /**
   * Returns an observable that emits when the script is loaded.
   */
  public onScriptLoaded() {
    return this.scriptLoaded$.asObservable();
  }

  /**
   * Check if Google Pay is available and ready to use
   */
  public isGooglePayAvailable(): boolean {
    return this.scriptLoaded$.value && 
           this.googlePayClient !== undefined && 
           typeof google !== 'undefined' && 
           google.payments && 
           google.payments.api;
  }

  /**
   * Get Google Pay availability status with detailed info
   */
  public getGooglePayStatus(): {
    available: boolean;
    scriptLoaded: boolean;
    clientInitialized: boolean;
    reason?: string;
  } {
    const scriptLoaded = this.scriptLoaded$.value;
    const clientInitialized = this.googlePayClient !== undefined;
    const googleApiAvailable = typeof google !== 'undefined' && google.payments && google.payments.api;
    
    if (!scriptLoaded) {
      return {
        available: false,
        scriptLoaded: false,
        clientInitialized: false,
        reason: 'Script failed to load (likely blocked by ad blocker or network issue)'
      };
    }
    
    if (!googleApiAvailable) {
      return {
        available: false,
        scriptLoaded: true,
        clientInitialized: false,
        reason: 'Google Pay API not available in loaded script'
      };
    }
    
    if (!clientInitialized) {
      return {
        available: false,
        scriptLoaded: true,
        clientInitialized: false,
        reason: 'Google Pay client not initialized'
      };
    }
    
    return {
      available: true,
      scriptLoaded: true,
      clientInitialized: true
    };
  }
}
